import { useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import cn from 'classnames';
// Hooks
import { useOutsideClick, useGetUnreadNotificationsCount } from 'hooks';
// Api
import apolloNotificationClient from 'api/apolloNotificationClient';
import {
  MARK_ALL_NOTIFICATIONS_READ,
  REMOVE_ALL_NOTIFICATIONS,
} from 'api/notifications/mutations';
import { GET_UNREAD_NOTIFICATIONS_COUNT } from 'api/notifications/queries';
// Types
import { RemoveAllNotifications } from 'api/notifications/types/RemoveAllNotifications';
import { MarkAllNotificationsRead } from 'api/notifications/types/MarkAllNotificationsRead';
// UI
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Text from 'ui3/Text/Text';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './MultipleNotificationsActions.module.scss';

type MultipleNotificationsActionsProps = {
  onUpdate: () => void;
  isSmall?: boolean; // flag used to show if this is navigation dropdown or mobile web view
};

const MultipleNotificationsActions = ({
  onUpdate,
  isSmall = false,
}: MultipleNotificationsActionsProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

  const { data } = useGetUnreadNotificationsCount();

  const [removeAllNotifications] = useMutation<RemoveAllNotifications>(
    REMOVE_ALL_NOTIFICATIONS,
    {
      client: apolloNotificationClient,
      refetchQueries: [
        {
          query: GET_UNREAD_NOTIFICATIONS_COUNT,
        },
      ],
    }
  );

  const [markAllNotificationsRead] = useMutation<MarkAllNotificationsRead>(
    MARK_ALL_NOTIFICATIONS_READ,
    {
      client: apolloNotificationClient,
      refetchQueries: [
        {
          query: GET_UNREAD_NOTIFICATIONS_COUNT,
        },
      ],
    }
  );

  const handleRemoveAllNotifications = async () => {
    try {
      await removeAllNotifications();
      await onUpdate();
    } catch (error) {
      showToast({
        message:
          (error as Error).message ||
          'Error clearing notifications. Refresh or try again later.',
        type: 'error',
      });
    }
  };

  const handleMarkNotificationsRead = async () => {
    try {
      await markAllNotificationsRead();
      await onUpdate();
    } catch (error) {
      showToast({
        message:
          (error as Error).message ||
          'Error updating notifications status. Refresh or try again later.',
        type: 'error',
      });
    }
  };

  useOutsideClick(dropdownRef, () => {
    setIsDropdownVisible(false);
  });

  const toggleDropdown = () => {
    setIsDropdownVisible((prev) => !prev);
  };

  const hasUnread = !!data?.getUnreadNotificationsCount;

  return (
    <div className={styles.root} ref={dropdownRef}>
      <button
        type="button"
        className={cn(styles.dropdownButton, {
          [styles.noBorder]: isSmall,
        })}
        onClick={toggleDropdown}
      >
        <MillionsIcon name="moreAlternate" className={styles.dotsIcon} />
      </button>

      <ul
        aria-hidden={!isDropdownVisible}
        className={cn(styles.dropdown, {
          [styles.visible]: isDropdownVisible,
          [styles.smallerGap]: isSmall,
        })}
      >
        {hasUnread && (
          <li>
            <button
              data-testid="remove-button"
              type="button"
              className={styles.option}
              onClick={handleMarkNotificationsRead}
            >
              <MillionsIcon
                name="doubleCheck"
                className={cn(styles.optionIcon, styles.fillIcon)}
              />
              <Text variant="subtitle2" color="lights-medium">
                Mark as read
              </Text>
            </button>
          </li>
        )}
        <li>
          <button
            data-testid="remove-button"
            type="button"
            className={styles.option}
            onClick={handleRemoveAllNotifications}
          >
            <MillionsIcon name="trash" className={styles.optionIcon} />
            <Text variant="subtitle2" color="lights-medium">
              Clear All
            </Text>
          </button>
        </li>
      </ul>
    </div>
  );
};

export default MultipleNotificationsActions;
